import { useState, useCallback, useMemo } from 'react'
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  type Node,
} from 'reactflow'
import 'reactflow/dist/style.css'
import { LayoutTemplate, GitBranch } from 'lucide-react'

import type { AgentConfigListItem, ReactFlowTraceExport } from '../../../lib/api'
import { AgentNode } from './nodes/AgentNode'
import { RouterNode } from './nodes/RouterNode'
import { SubgraphNode } from './nodes/SubgraphNode'
import { getStaticGraphData } from './staticGraphData'
import { buildTraceGraph } from './traceGraphData'
import { AGENT_ROLE_COLORS } from '../../../lib/agentColors'

const nodeTypes = {
  agentNode: AgentNode,
  routerNode: RouterNode,
  subgraphNode: SubgraphNode,
}

type CanvasMode = 'architecture' | 'trace'

interface StrategyCanvasProps {
  agents: AgentConfigListItem[]
  trace: ReactFlowTraceExport | null | undefined
  isTraceLoading: boolean
  onAgentSelect: (agentId: string) => void
}

export function StrategyCanvas({ agents: _agents, trace, isTraceLoading, onAgentSelect }: StrategyCanvasProps) {
  const [canvasMode, setCanvasMode] = useState<CanvasMode>('architecture')

  const { nodes: staticNodes, edges: staticEdges } = useMemo(() => getStaticGraphData(), [])
  const traceData = useMemo(() => (trace ? buildTraceGraph(trace) : null), [trace])

  const currentNodes = canvasMode === 'trace' && traceData ? traceData.nodes : staticNodes
  const currentEdges = canvasMode === 'trace' && traceData ? traceData.edges : staticEdges

  const [, , onNodesChange] = useNodesState(currentNodes)
  const [, , onEdgesChange] = useEdgesState(currentEdges)

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      const agentId = (node.data as { agentId?: string | null }).agentId
      if (agentId) {
        onAgentSelect(agentId)
      }
    },
    [onAgentSelect]
  )

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-slate-200 shrink-0">
        <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
          <button
            type="button"
            onClick={() => setCanvasMode('architecture')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              canvasMode === 'architecture'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <LayoutTemplate className="w-3.5 h-3.5" />
            Arquitectura
          </button>
          <button
            type="button"
            onClick={() => setCanvasMode('trace')}
            disabled={!trace && !isTraceLoading}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
              canvasMode === 'trace'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <GitBranch className="w-3.5 h-3.5" />
            {isTraceLoading ? 'Cargando traza...' : 'Ultima Traza'}
          </button>
        </div>
        <p className="text-xs text-slate-400">
          Click en un agente para editar su configuracion
        </p>
      </div>

      <div className="flex-1">
        <ReactFlow
          nodes={currentNodes}
          edges={currentEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={handleNodeClick}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.2}
          maxZoom={2}
          deleteKeyCode={null}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#E2E8F0" />
          <Controls className="bg-white border border-slate-200 rounded-lg shadow-sm" />
          <MiniMap
            nodeColor={(n) => {
              const role = (n.data as { agentRole?: string }).agentRole
              return AGENT_ROLE_COLORS[role ?? ''] ?? '#CBD5E1'
            }}
            maskColor="rgba(248,250,252,0.85)"
            className="bg-white border border-slate-200 rounded-lg shadow-sm"
          />
        </ReactFlow>
      </div>
    </div>
  )
}
